import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Block, FlexDiv } from '@awarego-ui/components';
import services from '../../services';
import { useStores } from '../../hooks/use-stores';
import AwareExposeForm from './AwareExposeForm';
import AwareExposeScanner, { ATTACK_STEPS } from './AwareExposeScanner';
import AwareExposeProfile from './AwareExposeProfile';
import AwareExposeAttacks from './AwareExposeAttacks';
import AwareExposeIntro from './AwareExposeIntro';

const PHASES = {
    INTRO: 'intro',
    FORM: 'form',
    SCANNING: 'scanning',
    PROFILE: 'profile',
    ATTACKS_LOADING: 'attacks_loading',
    ATTACKS: 'attacks',
};

function AwareExpose({ mode = 'public', currentCompany }) {
    const params = useParams();
    const { authStore } = useStores();
    const publicKey = params.publicKey || null;

    const [phase, setPhase] = useState(
        mode === 'authenticated' ? PHASES.INTRO : PHASES.FORM
    );
    const [config, setConfig] = useState(null);
    const [configError, setConfigError] = useState(null);
    const [profile, setProfile] = useState(null);
    const [emails, setEmails] = useState(null);
    const [runId, setRunId] = useState(null);
    const [error, setError] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [lastRun, setLastRun] = useState(null);
    const [person, setPerson] = useState(null);

    const companyId = useMemo(() => {
        if (mode === 'authenticated' && currentCompany) {
            return currentCompany.company_id;
        }
        return null;
    }, [mode, currentCompany]);

    // Load config (allowed domains, branding)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                let cfg;
                if (mode === 'public') {
                    cfg = await services.AwareExpose.publicConfig(publicKey);
                } else {
                    cfg = await services.AwareExpose.config(companyId);
                    if (!cfg || cfg.enabled === false) {
                        setConfigError(
                            'AwareExpose is not enabled for this company.'
                        );
                        return;
                    }
                }
                if (!cancelled) setConfig(cfg);
            } catch (e) {
                if (!cancelled) {
                    setConfigError(
                        e && e.response && e.response.data && e.response.data.error
                            ? e.response.data.error
                            : 'AwareExpose link not available.'
                    );
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [mode, publicKey, companyId]);

    // For authenticated mode, fetch the latest run on mount.
    useEffect(() => {
        if (mode !== 'authenticated' || !companyId) return;
        let cancelled = false;
        (async () => {
            try {
                const { run } = await services.AwareExpose.lastRun(companyId);
                if (cancelled) return;
                if (run) {
                    setLastRun(run);
                    setRunId(run.id);
                    setProfile({
                        exposure_level: run.exposure_level,
                        exposure_score: run.exposure_score,
                        signals_count: run.signals_count,
                        summary: run.summary,
                        signals: run.signals || [],
                    });
                    setPerson({
                        name: run.full_name,
                        title: run.job_title,
                        email: run.email,
                        links: run.links_json || [],
                    });
                    setEmails(run.attacks || null);
                    setPhase(
                        run.attacks && run.attacks.length
                            ? PHASES.ATTACKS
                            : run.exposure_level
                                ? PHASES.PROFILE
                                : PHASES.INTRO
                    );
                } else {
                    setPhase(PHASES.FORM);
                }
            } catch (e) {
                // No prior run is fine; fall through to form.
                if (!cancelled) setPhase(PHASES.FORM);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [mode, companyId]);

    const runScanCall = async (formValues) => {
        setError(null);
        setWarnings([]);
        setProfile(null);
        setEmails(null);
        setPhase(PHASES.SCANNING);
        const payload = {
            name: formValues.name.trim(),
            title: formValues.title.trim(),
            email: formValues.email.trim().toLowerCase(),
            links: (formValues.links || [])
                .map((l) => String(l || '').trim())
                .filter(Boolean),
        };
        setPerson(payload);
        try {
            const res =
                mode === 'public'
                    ? await services.AwareExpose.publicScan(publicKey, payload)
                    : await services.AwareExpose.scan(companyId, payload);
            if (mode !== 'public' && res.runId) setRunId(res.runId);
            setProfile(res.profile);
            setWarnings(Array.isArray(res.warnings) ? res.warnings : []);
            setPhase(PHASES.PROFILE);
        } catch (e) {
            setError(extractMessage(e));
            setPhase(PHASES.FORM);
        }
    };

    const runAttacksCall = async () => {
        setError(null);
        setWarnings([]);
        setPhase(PHASES.ATTACKS_LOADING);
        try {
            const res =
                mode === 'public'
                    ? await services.AwareExpose.publicAttacks(publicKey, {
                          person,
                          profile,
                      })
                    : await services.AwareExpose.attacks(companyId, runId);
            setEmails(res.emails || []);
            setWarnings(Array.isArray(res.warnings) ? res.warnings : []);
            if (!res.emails || res.emails.length === 0) {
                setError(
                    res.error ||
                        'AwareExpose could not generate any attack emails.'
                );
                setPhase(PHASES.PROFILE);
                return;
            }
            setPhase(PHASES.ATTACKS);
        } catch (e) {
            setError(extractMessage(e));
            setWarnings(extractWarnings(e));
            setPhase(PHASES.PROFILE);
        }
    };

    const handleStart = () => {
        setProfile(null);
        setEmails(null);
        setRunId(null);
        setError(null);
        setWarnings([]);
        setPhase(PHASES.FORM);
    };

    const wideLayout =
        phase === PHASES.PROFILE ||
        phase === PHASES.ATTACKS_LOADING ||
        phase === PHASES.ATTACKS;
    const blockWidth = wideLayout ? 760 : 640;

    if (configError) {
        return (
            <PageWrapper width={640}>
                <AwareExposeIntro
                    mode={mode}
                    error={configError}
                    onStart={null}
                />
            </PageWrapper>
        );
    }
    if (!config) {
        return (
            <PageWrapper width={640}>
                <AwareExposeIntro mode={mode} loading />
            </PageWrapper>
        );
    }

    return (
        <PageWrapper width={blockWidth}>
            {phase === PHASES.INTRO && (
                <AwareExposeIntro
                    mode={mode}
                    onStart={handleStart}
                    lastRun={lastRun}
                />
            )}
            {phase === PHASES.FORM && (
                <AwareExposeForm
                    config={config}
                    defaultEmail={
                        mode === 'authenticated' &&
                        authStore.currentUser &&
                        authStore.currentUser.email
                    }
                    defaultName={
                        (mode === 'authenticated' &&
                            authStore.currentUser &&
                            authStore.currentUser.name) ||
                        ''
                    }
                    error={error}
                    onSubmit={runScanCall}
                />
            )}
            {phase === PHASES.SCANNING && <AwareExposeScanner />}
            {phase === PHASES.ATTACKS_LOADING && (
                <AwareExposeScanner
                    title="Crafting your personalised attack emails"
                    subtitle="Combining your exposure signals into three educational phishing scenarios…"
                    steps={ATTACK_STEPS}
                />
            )}
            {(phase === PHASES.PROFILE || phase === PHASES.ATTACKS) &&
                profile && (
                    <AwareExposeProfile
                        person={person}
                        profile={profile}
                        loadingAttacks={false}
                        onGenerateAttacks={
                            phase === PHASES.PROFILE
                                ? runAttacksCall
                                : undefined
                        }
                        error={error}
                        warnings={warnings}
                    />
                )}
            {phase === PHASES.ATTACKS && emails && (
                <AwareExposeAttacks
                    emails={emails}
                    canRerun={mode === 'authenticated'}
                    onRerun={handleStart}
                />
            )}
        </PageWrapper>
    );
}

function PageWrapper({ width, children }) {
    return (
        <FlexDiv
            wrapper
            column
            alignCenter
            justifyCenter
            style={{ padding: '20px 16px 40px' }}
        >
            <Block width={width}>{children}</Block>
        </FlexDiv>
    );
}

function extractMessage(e) {
    if (e && e.response && e.response.data) {
        return (
            e.response.data.error ||
            e.response.data.message ||
            e.message ||
            'Unknown error'
        );
    }
    return (e && e.message) || 'Unknown error';
}

function extractWarnings(e) {
    if (
        e &&
        e.response &&
        e.response.data &&
        Array.isArray(e.response.data.warnings)
    ) {
        return e.response.data.warnings;
    }
    return [];
}

export default observer(AwareExpose);
